// src/components/settings/ImageSettingsTab.jsx - Image Generation Settings
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { settingsAPI } from '../../services/api';
import {
    SparklesIcon,
    PlusIcon,
    XMarkIcon,
    ArrowPathIcon,
    EyeIcon,
    PaintBrushIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const PRESET_STYLES = [
    {
        name: 'Corporate Clean',
        keywords: 'white, blue, orange, low poly, clean, minimal, professional',
        description: 'Clean, professional style perfect for business content'
    },
    {
        name: 'Modern Tech',
        keywords: 'gradient, neon, dark background, tech, futuristic, glowing',
        description: 'Modern technology aesthetic with vibrant colors'
    },
    {
        name: 'Warm & Friendly',
        keywords: 'warm colors, soft lighting, friendly, approachable, rounded shapes',
        description: 'Warm and inviting style for personal brands'
    },
    {
        name: 'Bold & Dynamic',
        keywords: 'bold colors, dynamic composition, energetic, striking, high contrast',
        description: 'Eye-catching style for high-impact content'
    },
    {
        name: 'Minimalist',
        keywords: 'minimal, white space, simple, elegant, typography focused',
        description: 'Clean minimalist approach with focus on simplicity'
    },
    {
        name: 'Creative Art',
        keywords: 'artistic, creative, colorful, abstract, expressive, painterly',
        description: 'Artistic and creative style for creative industries'
    }
];

function ImageSettingsTab() {
    const [customKeywords, setCustomKeywords] = useState('');
    const [selectedPreset, setSelectedPreset] = useState('');
    const [previewMode, setPreviewMode] = useState(false);
    const queryClient = useQueryClient();

    const { data: settings, isLoading } = useQuery(
        'imageSettings',
        () => settingsAPI.getImageSettings(),
        {
            retry: 1,
            onError: (error) => {
                console.error('Image settings error:', error);
                if (error.response?.status === 401) {
                    toast.error('Please log in again');
                }
            }
        }
    );

    const updateMutation = useMutation(settingsAPI.saveImageSettings, {
        onSuccess: (response) => {
            queryClient.invalidateQueries('imageSettings');
            toast.success('Image settings saved! Your new style will be used for future image generations.');
            // console.log('✅ Image settings saved successfully:', response.data);
        },
        onError: (error) => {
            console.error('Update image settings error:', error);
            toast.error(error.response?.data?.message || 'Failed to save settings');
        },
    });

    // Load current settings
    useEffect(() => {
        if (settings?.data?.data) {
            const imageSettings = settings.data.data.imageKeywords || 'white, blue, orange, low poly';
            setCustomKeywords(imageSettings);
            // console.log('📸 Loaded image settings:', imageSettings);
        }
    }, [settings]);

    const handleSave = () => {
        if (!customKeywords.trim()) {
            toast.error('Please enter some keywords');
            return;
        }

        updateMutation.mutate({
            imageKeywords: customKeywords.trim()
        });
    };

    const handlePresetSelect = (preset) => {
        setCustomKeywords(preset.keywords);
        setSelectedPreset(preset.name);
    };

    const handleAddKeyword = (keyword) => {
        if (!keyword.trim()) return;

        const current = customKeywords ? customKeywords + ', ' : '';
        setCustomKeywords(current + keyword.trim());
    };

    const handleRemoveKeyword = (indexToRemove) => {
        const keywords = customKeywords.split(',').map(k => k.trim());
        const filtered = keywords.filter((_, index) => index !== indexToRemove);
        setCustomKeywords(filtered.join(', '));
    };

    const getKeywordArray = () => {
        return customKeywords
            .split(',')
            .map(k => k.trim())
            .filter(k => k.length > 0);
    };

    const generatePreview = () => {
        const keywords = getKeywordArray();
        return `Example prompt: "AI-powered social media dashboard, ${keywords.join(', ')}"`;
    };

    if (isLoading) {
        return <div className="animate-pulse">Loading image settings...</div>;
    }

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-medium text-gray-900">Image Generation Settings</h3>
                <p className="mt-1 text-sm text-gray-600">
                    Customize the visual style and keywords that drive AI image generation for your posts
                </p>
            </div>

            {/* Current Settings Display */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                    <h4 className="text-md font-medium text-gray-900">Current Style Keywords</h4>
                    <button
                        onClick={() => setPreviewMode(!previewMode)}
                        className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                        <EyeIcon className="h-4 w-4 mr-2" />
                        {previewMode ? 'Hide' : 'Show'} Preview
                    </button>
                </div>

                <div className="space-y-4">
                    <div>
                        <label htmlFor="keywords" className="block text-sm font-medium text-gray-700 mb-2">
                            Style Keywords (comma-separated)
                        </label>
                        <textarea
                            id="keywords"
                            value={customKeywords}
                            onChange={(e) => setCustomKeywords(e.target.value)}
                            rows={3}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            placeholder="e.g., white, blue, orange, low poly, minimal, professional"
                        />
                        <p className="mt-1 text-sm text-gray-500">
                            These keywords will be added to every image generation prompt to maintain consistent visual style
                        </p>
                    </div>

                    {/* Keyword Tags Display */}
                    {customKeywords && (
                        <div>
                            <p className="text-sm font-medium text-gray-700 mb-2">Current Keywords:</p>
                            <div className="flex flex-wrap gap-2">
                                {getKeywordArray().map((keyword, index) => (
                                    <span
                                        key={index}
                                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                    >
                                        {keyword}
                                        <button
                                            onClick={() => handleRemoveKeyword(index)}
                                            className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full text-blue-600 hover:bg-blue-200"
                                        >
                                            <XMarkIcon className="h-3 w-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Preview */}
                    {previewMode && (
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h5 className="text-sm font-medium text-gray-700 mb-2">Preview Prompt:</h5>
                            <p className="text-sm text-gray-600 italic">
                                {generatePreview()}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Preset Styles */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h4 className="text-md font-medium text-gray-900 mb-4">Preset Styles</h4>
                <p className="text-sm text-gray-600 mb-6">
                    Choose from predefined styles or use them as inspiration for your custom keywords
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {PRESET_STYLES.map((preset, index) => (
                        <div
                            key={index}
                            className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                                selectedPreset === preset.name
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                            onClick={() => handlePresetSelect(preset)}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <h5 className="font-medium text-gray-900">{preset.name}</h5>
                                <PaintBrushIcon className="h-5 w-5 text-gray-400" />
                            </div>
                            <p className="text-sm text-gray-600 mb-3">{preset.description}</p>
                            <div className="text-xs text-gray-500 font-mono bg-gray-100 p-2 rounded">
                                {preset.keywords}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Quick Add Keywords */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h4 className="text-md font-medium text-gray-900 mb-4">Quick Add Keywords</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                    {[
                        'minimal', 'modern', 'vintage', 'colorful', 'monochrome', 'gradient',
                        'geometric', 'organic', 'textured', 'smooth', 'bold', 'subtle',
                        'warm', 'cool', 'bright', 'dark', 'professional', 'creative',
                        'tech', 'nature', 'urban', 'abstract', 'realistic', 'stylized'
                    ].map((keyword) => (
                        <button
                            key={keyword}
                            onClick={() => handleAddKeyword(keyword)}
                            className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-colors"
                        >
                            <PlusIcon className="h-3 w-3 inline mr-1" />
                            {keyword}
                        </button>
                    ))}
                </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
                <button
                    onClick={handleSave}
                    disabled={updateMutation.isLoading}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                    {updateMutation.isLoading ? (
                        <>
                            <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                            Saving...
                        </>
                    ) : (
                        <>
                            <img src="/images/Bob_only.svg" alt="PostMgr" className="h-4 w-4" />
                            Save Image Settings
                        </>
                    )}
                </button>
            </div>

            {/* Tips */}
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
                <div className="flex">
                    <div className="flex-shrink-0">
                        <span className="text-green-400 text-lg">💡</span>
                    </div>
                    <div className="ml-3">
                        <h3 className="text-sm font-medium text-green-800">Pro Tips for Better Images</h3>
                        <div className="mt-2 text-sm text-green-700">
                            <ul className="list-disc list-inside space-y-1">
                                <li><strong>Be Specific:</strong> Use specific style terms like "low poly" or "minimalist" rather than vague terms</li>
                                <li><strong>Color Consistency:</strong> Include your brand colors to maintain visual consistency</li>
                                <li><strong>Style Direction:</strong> Add style descriptors like "professional", "modern", or "creative"</li>
                                <li><strong>Medium Type:</strong> Specify the type like "illustration", "photograph", or "digital art"</li>
                                <li><strong>Test & Iterate:</strong> Generate a few posts to see how your keywords affect the output</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ImageSettingsTab;